import dayjs from 'dayjs';

import type { TradesQuery } from '@benzinga/gov-trades';

export interface ToolbarState {
  ticker: string;
  chamber: '' | 'House' | 'Senate';
  datePreset: string;
  sort: string;
}

export const PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;
export const MAX_TICKER_LENGTH = 24;

export const CHAMBERS = ['House', 'Senate'] as const;

export const DATE_PRESETS = [
  { label: 'Last 30 Days', value: '30' },
  { label: 'Last 90 Days', value: '90' },
  { label: 'Year to Date', value: 'ytd' },
  { label: 'All Time', value: 'all' },
] as const;

export const SORT_OPTIONS = [
  { label: 'Report Date: New to Old', value: 'report_date:desc' },
  { label: 'Report Date: Old to New', value: 'report_date:asc' },
  { label: 'Trade Date: New to Old', value: 'transaction_date:desc' },
  { label: 'Trade Date: Old to New', value: 'transaction_date:asc' },
] as const;

export const SORT_VALUES: readonly string[] = SORT_OPTIONS.map(option => option.value);

export const defaultToolbar: ToolbarState = { ticker: '', chamber: '', datePreset: '90', sort: '' };

export const lockedTickerToolbar = (ticker: string): ToolbarState => ({
  ...defaultToolbar,
  datePreset: 'all',
  ticker: (ticker ?? '').trim().slice(0, MAX_TICKER_LENGTH).toUpperCase(),
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const presetToDateFrom = (preset: string): string | undefined => {
  if (preset === 'all') return undefined;
  if (preset === 'ytd') return dayjs().startOf('year').format('YYYY-MM-DD');
  return dayjs()
    .subtract(preset === '30' ? 30 : 90, 'day')
    .format('YYYY-MM-DD');
};

export const buildQuery = (state: ToolbarState, nextPage: number): TradesQuery => ({
  chamber: state.chamber || undefined,
  dateFrom: presetToDateFrom(state.datePreset),
  page: nextPage,
  pageSize: PAGE_SIZE,
  sort: state.sort || undefined,
  ticker: state.ticker || undefined,
});

export const sanitizeTradesQuery = (query: TradesQuery = {}): TradesQuery => ({
  chamber: CHAMBERS.includes(query.chamber as (typeof CHAMBERS)[number]) ? query.chamber : undefined,
  dateFrom: typeof query.dateFrom === 'string' && ISO_DATE.test(query.dateFrom) ? query.dateFrom : undefined,
  dateTo: typeof query.dateTo === 'string' && ISO_DATE.test(query.dateTo) ? query.dateTo : undefined,
  page: typeof query.page === 'number' && Number.isFinite(query.page) ? Math.max(0, Math.trunc(query.page)) : 0,
  pageSize:
    typeof query.pageSize === 'number' && Number.isFinite(query.pageSize)
      ? Math.min(MAX_PAGE_SIZE, Math.max(1, Math.trunc(query.pageSize)))
      : PAGE_SIZE,
  sort: typeof query.sort === 'string' && SORT_VALUES.includes(query.sort) ? query.sort : undefined,
  ticker:
    typeof query.ticker === 'string'
      ? query.ticker.trim().slice(0, MAX_TICKER_LENGTH).toUpperCase() || undefined
      : undefined,
});
